import { useEffect } from "react";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";

interface NotificationClickPayload {
	click_id?: string;
	link_url: string;
	type: string;
	tag_text: string;
}

const PUSH_ANALYTICS_CACHE = "push-analytics";
const PUSH_ANALYTICS_PENDING_REQUEST = "/__push-last-click__";

export const usePushNotificationClickAnalytics = () => {
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	useEffect(() => {
		const processedClickIds = new Set<string>();

		const getAnalyticsCache = async () => {
			if (!("caches" in window)) {
				return null;
			}

			return caches.open(PUSH_ANALYTICS_CACHE);
		};

		const deletePendingPayload = async (cache?: Cache | null) => {
			const activeCache = cache || (await getAnalyticsCache());
			if (!activeCache) {
				return;
			}

			await activeCache.delete(PUSH_ANALYTICS_PENDING_REQUEST);
		};

		const reportPayload = (payload: NotificationClickPayload) => {
			if (payload.click_id && processedClickIds.has(payload.click_id)) {
				return;
			}

			if (payload.click_id) {
				processedClickIds.add(payload.click_id);
			}

			// console.log("[Push] sending pwa_push_opened analytics", {
			// 	event_name: "pwa_push_opened",
			// 	event_type: "pwa_push",
			// 	action: "opened",
			// 	source: "pwa_push",
			// 	event_id: payload.click_id || undefined,
			// 	link_url: payload.link_url || undefined,
			// 	type: payload.type || undefined,
			// 	tag_text: payload.tag_text || undefined,
			// 	page_location: window.location.pathname
			// });

			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "pwa_push_opened",
				event_type: "pwa_push",
				action: "opened",
				source: "pwa_push",
				event_id: payload.click_id || undefined,
				link_url: payload.link_url || undefined,
				type: payload.type || undefined,
				tag_text: payload.tag_text || undefined,
				page_location: window.location.pathname
			});
		};

		const consumePendingPayload = async () => {
			const cache = await getAnalyticsCache();
			if (!cache) {
				return;
			}
			const response = await cache.match(PUSH_ANALYTICS_PENDING_REQUEST);

			if (!response) {
				return;
			}

			let payload: NotificationClickPayload;
			try {
				payload = (await response.json()) as NotificationClickPayload;
			} catch (_error) {
				await deletePendingPayload(cache);
				return;
			}

			if (!payload) {
				return;
			}

			reportPayload(payload);
			await deletePendingPayload(cache);
		};

		const handleServiceWorkerMessage = (event: MessageEvent) => {
			// console.log("[Push] service worker message", event.data);

			if (!event.data || event.data.type !== "NOTIFICATION_CLICKED") {
				return;
			}

			const payload = event.data.payload as NotificationClickPayload;
			if (!payload) {
				return;
			}

			reportPayload(payload);
			void deletePendingPayload();
		};

		if (!("serviceWorker" in navigator)) {
			return;
		}

		navigator.serviceWorker.addEventListener(
			"message",
			handleServiceWorkerMessage
		);
		void consumePendingPayload();

		return () => {
			navigator.serviceWorker.removeEventListener(
				"message",
				handleServiceWorkerMessage
			);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
